import { FC, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import dayjs, { Dayjs } from "dayjs";

import Modal, { ModalChildrenFnPropsI } from "shared/ui/modal";
import CalendarForm, { CalendarDateI } from "shared/ui/calendar-form";
import { getCanonicalMeetingTime } from "shared/lib/helpers";
import { combineDateTimeWithZone } from "shared/lib/helpers/date";
import {
  US_ZONE_NAME_TO_TZ_CODE,
  UsZoneName,
} from "shared/lib/constants/timezones";
import { AccountDetailsStatusI } from "shared/lib/interfaces/account";

import { ERRORS, MEETING_MOVE_ERROR_CODES } from "@/constants/errors";
import { useMutationMoveMeeting } from "@/modules/pipeline/account-details/data/mutations";
import { AccountDetailsQueryKeys } from "@/modules/pipeline/account-details/data/keys";
import { useModifyMeeting } from "@/modules/pipeline/account-details/hooks/use-modify-meeting";

export const MOVE_MEETING_MODAL_ID = "move-meeting-modal";

export const MOVE_MEETING_SUCCESS_MESSAGE =
  "Meeting moved, attendees were notified";

const MOVE_MEETING_DEFAULT_ERROR_MESSAGE =
  "Failed to move the meeting. Please try again.";

const MISSING_CONTEXT_ERROR_MESSAGE =
  "This meeting cannot be moved from here. Please use Modify meeting.";

export const MOVE_MEETING_SAME_TIME_HINT =
  "Pick a different time to move the meeting";

const MINUTE_MS = 60_000;

// After these the move can never succeed for this event, so the rep is
// offered the booking flow (new slot, old one cancelled) instead.
const MODIFY_FALLBACK_ERROR_CODES: number[] = [
  MEETING_MOVE_ERROR_CODES.EVENT_GONE,
  MEETING_MOVE_ERROR_CODES.NOT_ORGANIZER,
];

interface MoveMeetingModalPropsI {
  campaignId?: string;
  accountId?: string;
  // The lead that holds the meeting, see getMeetingContactId.
  contactId?: string;
  accountStatus?: AccountDetailsStatusI;
  // Called whenever the AE's event really moved, so the drawer reloads: on
  // success, and on RECORD_PENDING (the event moved, the booking lags).
  onMeetingChanged?: () => void;
}

interface MoveFailureI {
  code?: number;
  message: string;
}

const getErrorCode = (error: unknown): number | undefined => {
  if (axios.isAxiosError<{ error_code?: number }>(error)) {
    return error.response?.data?.error_code;
  }

  return undefined;
};

const describeFailure = (error: unknown): MoveFailureI => {
  const code = getErrorCode(error);

  if (code !== undefined && ERRORS[code]) {
    return { code, message: ERRORS[code] };
  }

  return { code, message: MOVE_MEETING_DEFAULT_ERROR_MESSAGE };
};

/**
 * The picked slot as an instant, or null while it cannot be one: no time
 * option left for the day, or a zone/date the combine helper rejects. A null
 * pick is never "the same time", so the button stays enabled and the submit
 * path reports the combine error to the rep.
 */
const getPickedInstant = (values: CalendarDateI | undefined): Dayjs | null => {
  if (!values?.time) {
    return null;
  }

  try {
    return combineDateTimeWithZone(values.date, values.time, values.timezone);
  } catch {
    return null;
  }
};

const isSameMinute = (picked: Dayjs | null, meetingTime: string | null) => {
  if (!picked || !meetingTime) {
    return false;
  }

  const meeting = dayjs(meetingTime);

  if (!meeting.isValid()) {
    return false;
  }

  return (
    Math.floor(picked.valueOf() / MINUTE_MS) ===
    Math.floor(meeting.valueOf() / MINUTE_MS)
  );
};

const MoveMeetingModalContent: FC<
  MoveMeetingModalPropsI & Pick<ModalChildrenFnPropsI, "handleClose">
> = ({
  campaignId,
  accountId,
  contactId,
  accountStatus,
  onMeetingChanged,
  handleClose,
}) => {
  const queryClient = useQueryClient();
  const { mutateAsync: moveMeeting, isPending } = useMutationMoveMeeting();
  const modifyMeeting = useModifyMeeting();

  // The raw pick; the form reports it on mount and on every change.
  const [calendarValues, setCalendarValues] = useState<CalendarDateI>();
  const [failure, setFailure] = useState<MoveFailureI | null>(null);

  const canonicalMeetingTime = accountStatus
    ? getCanonicalMeetingTime(accountStatus)
    : null;

  const canFallBackToModify =
    failure?.code !== undefined &&
    MODIFY_FALLBACK_ERROR_CODES.includes(failure.code);

  // The picker opens on the current time, and moving a meeting onto its own
  // minute is a no-op the backend refuses (SAME_TIME), so it is not offered.
  const isPickUnchanged = isSameMinute(
    getPickedInstant(calendarValues),
    canonicalMeetingTime
  );

  const invalidateCalendarEvent = (
    eventCampaignId: string,
    eventAccountId: string
  ) => {
    void queryClient.invalidateQueries({
      queryKey: AccountDetailsQueryKeys.bookedMeetingCalendarEvent(
        eventCampaignId,
        eventAccountId
      ),
    });
  };

  const handleSubmit = async () => {
    if (!campaignId || !accountId || !contactId || !calendarValues) {
      setFailure({ message: MISSING_CONTEXT_ERROR_MESSAGE });

      return;
    }

    setFailure(null);

    try {
      // Inside the try on purpose: the combine helper throws on a zone or
      // date it does not know, and that must surface as a message, not as a
      // crash of the drawer.
      const meetingTime = combineDateTimeWithZone(
        calendarValues.date,
        calendarValues.time,
        calendarValues.timezone
      ).toISOString();

      await moveMeeting({
        campaignId,
        accountId,
        contactId,
        meetingTime,
        meetingTimezone:
          US_ZONE_NAME_TO_TZ_CODE[calendarValues.timezone as UsZoneName],
      });

      toast.success(MOVE_MEETING_SUCCESS_MESSAGE);
      onMeetingChanged?.();
      handleClose();
    } catch (error) {
      const described = describeFailure(error);

      setFailure(described);

      if (described.code === MEETING_MOVE_ERROR_CODES.RECORD_PENDING) {
        // The AE's event did move; only the booking lags behind. The card and
        // the status pill must show the event as it is now.
        invalidateCalendarEvent(campaignId, accountId);
        onMeetingChanged?.();

        return;
      }

      if (described.code === MEETING_MOVE_ERROR_CODES.CONFLICT) {
        invalidateCalendarEvent(campaignId, accountId);
      }
    }
  };

  const handleModifyInstead = () => {
    handleClose();
    modifyMeeting();
  };

  return (
    <div className="flex flex-col gap-4">
      {failure && (
        <div
          role="alert"
          className="flex flex-col items-start gap-2 rounded-lg bg-red-50 p-3 text-red-800 typography-body-4"
        >
          <p>{failure.message}</p>

          {canFallBackToModify && (
            <button
              type="button"
              className="underline typography-body-4-medium"
              onClick={handleModifyInstead}
            >
              Modify meeting instead
            </button>
          )}
        </div>
      )}

      <CalendarForm
        bookingDeadlineInMonths={false}
        isNotes={false}
        initialDate={canonicalMeetingTime ?? undefined}
        initialTime={canonicalMeetingTime ?? undefined}
        onDateChange={setCalendarValues}
        onFormSubmit={handleSubmit}
        isLoading={isPending}
        cancelButton={{
          type: "button",
          text: "Cancel",
          onClick: handleClose,
        }}
        successButton={{
          type: "button",
          text: isPending ? "Moving..." : "Move meeting",
          disabled: isPending || isPickUnchanged,
        }}
      />

      {isPickUnchanged && (
        <p role="status" className="text-right text-black/60 typography-body-5">
          {MOVE_MEETING_SAME_TIME_HINT}
        </p>
      )}
    </div>
  );
};

/**
 * GLE-8249: reschedules a detected meeting by moving the AE's calendar event
 * in place, so the prospect gets one "updated" invite instead of a new
 * invite plus a cancellation. Rendered by the calendar-event card, which only
 * offers it when the backend reports can_move_event.
 */
export const MoveMeetingModal: FC<MoveMeetingModalPropsI> = (props) => (
  <Modal
    id={MOVE_MEETING_MODAL_ID}
    title="Reschedule meeting"
    description="Pick a new time. The existing calendar event is moved and every attendee receives an updated invite."
    modalContainerClassName="w-[520px]"
  >
    {({ handleClose }) => (
      <MoveMeetingModalContent {...props} handleClose={handleClose} />
    )}
  </Modal>
);
